import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { PaymentMapper } from './payment.mapper';
import { IPaymentAccountsEntity } from '../../model/api/payment-accounts/payment-accounts.entity.interface';
import { IValidateCardResponseEntity } from '../../model/api/payment-accounts/validate-card-response.entity.interface';
import { IPaymentAccounts } from '../../model/models/payment-methods/payment-accounts.interface';
import { IBankVerification } from '../../model/models/payment-methods/bank-verification.interface';
import { CardUpdate, IBankAccount, ICard, BankAccountUpdate } from '../../model/models/payment-methods';
import { Platform } from 'ionic-angular';
import { HttpProvider } from '../http/http';
import { LoggerProvider, LoggerEvent, LoggerLevel } from '../logger/logger';
import { IOrder } from '../../model/models/order/order-interface';
import { ScriptProvider } from '../script/script';
import { RouteProvider } from '../route/route';
import { CardInfo } from '../../model/models/card/card-info.terface';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { SegmentProvider } from '../analytics/segment';
import { LoginProvider } from '../myria/login-provider';
import { IPlaidRequest, IPlaidUpdateResponse } from '../../model/api/payment-accounts/bank-verification.interface';
import { IValidateOrderResponse } from '../../model/models/order/validate-order-response.interface';
import { ICardProcessor, ICardThreeDSecureType } from '../../model/models/payment-methods/card.interface';
import { DirectBankBrand, PaymentMethodType } from '../../model/types/payment-method-type';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { BankAccountAddAllowed, BankAccountVerificationStatus } from '../../model/types/bank-account-verification-status';
import { BankAccountMicroDepositStatus } from '../../model/types/bank-account-micro-deposits-status';
import { PaymentCardType } from '../../model/types/payment-card-type';
import { ResourceProvider } from '../resource/resource';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';
import { AGENT_SETTINGS, CARD_TYPE } from '../myria/app-variables';
import { FormControlProvider } from '../formbuilder/formcontrol';
import { TranslateService } from '@ngx-translate/core';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { LoadingProvider } from '../loading/loading';
import { MissingTransactionInfoType } from '../../model/types/missing-transaction-info-type';
import { IFieldVariableFeesEntity } from 'model/api/price-calculator/calculate-order-response-variable-fees.entity.interface';

declare let Cardinal;

@Injectable()
export class PaymentProvider {
  initialized: boolean = false;
  initPromise: Promise<any>;
  newCard;
  newBank;
  deletedAccountNumber;
  maxCardsAllowed: number = 3;
  maxBanksAllowed: number = 3;
  paymentAccounts: IPaymentAccounts;
  appleOrGoogleWalletCreditAccounts: IFieldVariableFeesEntity[];

  lastPaymentAccountsListDate = null;

  constructor(private httpProvider: HttpProvider,
              private routeProvider: RouteProvider,
              private scriptProvider: ScriptProvider,
              private analytics: SegmentProvider,
              private resourceProvider: ResourceProvider,
              private utilsProvider: UtilsProvider,
              private loginProvider: LoginProvider,
              private formProvider: FormControlProvider,
              private translate: TranslateService,
              private loading: LoadingProvider,
              private platform?: Platform) {
  }

  logout() {
    this.initialized = false;
    this.initPromise = null;
    this.paymentAccounts = null;
    this.lastPaymentAccountsListDate = null;
  }

  setNewCard(card) {
    this.newCard = card;
  }

  getNewCard() {
    return this.newCard;
  }

  setNewBank(bank) {
    this.newBank = bank;
  }

  getNewBank() {
    return this.newBank;
  }

  setDeletedAccountNumber(number) {
    this.deletedAccountNumber = number;
  }

  getDeletedAccountNumber() {
    return this.deletedAccountNumber;
  }

  initializePaymentAccounts(forceReload: boolean = false, initResolve = null) {
    if (this.initialized && !forceReload)
      return;
    else if (this.initPromise && !initResolve)
      return;

    const calls = [];
    // deprecated
    // calls.push(this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}account/customerpaymentaccounts`));
    const useCustomerPaymentAccountsV2 = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.UseCustomerPaymentAccountsV2); //!! only for development branch, so QA won't be blocked
    if(useCustomerPaymentAccountsV2)
      calls.push(this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}account/customerpaymentaccounts/v2`));
    else
      calls.push(this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}account/customerpaymentaccounts`));

    if (!this.initPromise)
      this.initPromise = new Promise((resolve) => {
        if (!initResolve)
          initResolve = resolve;
      });
    this.httpProvider.multipleCalls(calls, ([accounts]: [IPaymentAccountsEntity]) => {
      this.initPromise = null;
      this.initialized = true;
      this.paymentAccounts = PaymentMapper.fromPaymentAccountsEntity(accounts);
      this.lastPaymentAccountsListDate = new Date().getTime();
      initResolve();
      return true;
    }, (err) => {
      if (err.status === 401 || err.status === 403) // these are already handled and shouldn't cause a reload
        this.initPromise = null;
      else
        window.setTimeout(() => {
          this.initializePaymentAccounts(true, initResolve);
        }, 10000);
    });
  }

  ensureInitializedPaymentAccounts() {
    this.initializePaymentAccounts();
    if (this.initPromise)
      return this.initPromise;
    return null;
  }

  getCardType(binNumber: string, ignoreIssuingCountry = false): Promise<CardInfo> {
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}account/card/get-bin-info`, { BinNumber: binNumber, IgnoreIssuingCountry: ignoreIssuingCountry });
  }

  async isCardAddAllowed(paymentMethod, storedPaymentMethodsAdyen?) {
      if (!storedPaymentMethodsAdyen) {
      let loading = false;
      if (!this.initialized && !this.loading.showingLoading) {
        loading = true;
        this.loading.show();
      }

      await this.ensureInitializedPaymentAccounts();

      if (loading)
        this.loading.hide();
    }
      if (paymentMethod === PaymentMethodType.CreditCard)
        return this.getCardLimit(false, storedPaymentMethodsAdyen)
      if (paymentMethod === PaymentMethodType.DebitCard)
        return this.getCardLimit(true, storedPaymentMethodsAdyen);
      else
        LoggerProvider.error(this, LoggerEvent.Payment, 'Incorrect payment method to check card limit', paymentMethod);
  }

  getCardLimit(isDebit: boolean, storedPaymentMethodsAdyen?) {
    var cards;
    if (storedPaymentMethodsAdyen) {
      cards = storedPaymentMethodsAdyen.filter((card) => card.isDebit == isDebit)
    }
    else {
      if (!this.paymentAccounts) {
        this.ensureInitializedPaymentAccounts();
      }
      cards = this.paymentAccounts.cards.filter((card) => card.isDebit == isDebit)
    }
    if (cards.length < this.maxCardsAllowed)
      return true;
    else
      return false;
  }

  async getPaymentAccounts(forceReload: boolean = false): Promise<IPaymentAccounts> {
    if (forceReload && this.lastPaymentAccountsListDate) {
      const diff = (new Date().getTime() - this.lastPaymentAccountsListDate) / 1000;
      if (diff < 120) {
        const skip = await this.utilsProvider.hasFeatureFlag(FeatureFlagTypes.SkipPaymentAccountsListOverride);
        if (skip)
          LoggerProvider.info(this, `Skipped override payment accounts list force reload although threshold is too low: ${diff} seconds`);
        else {
          LoggerProvider.info(this, `Override payment accounts list force reload since threshold is too low: ${diff} seconds`);
          forceReload = false;
        }
      }
    }
    if (forceReload) {
      this.initializePaymentAccounts(true);
    }
    await this.ensureInitializedPaymentAccounts();
    return this.paymentAccounts;
  }

  async hasPostedAccounts() {
    const paymentAccounts = await this.getPaymentAccounts();
    let postedAccounts: IBankAccount[] = [];
    if (paymentAccounts.bankAccounts.length > 0) {
      paymentAccounts.bankAccounts.forEach(bank => {
        if(!bank.isDisabled && bank.verificationStatus != BankAccountVerificationStatus.Verified && bank.bankAccountMicroDepositStatus == BankAccountMicroDepositStatus.Posted)
          postedAccounts.push(bank);
      })
    }
    return postedAccounts.length;
  }

  isRelinkEligible(bank: IBankAccount): boolean {
    return !!bank && bank.plaidLinkEligible === true;
  }

  getRelinkEligibleCount(accounts: IPaymentAccounts): number {
    if (!accounts || !accounts.bankAccounts)
      return 0;
    return accounts.bankAccounts.filter(bank => this.isRelinkEligible(bank)).length;
  }

  addBankAccount(account,isPlaidAccount=false): Promise<Partial<IBankAccount>> {
    const entity = isPlaidAccount==false ? PaymentMapper.formToBankAccountEntity(account):PaymentMapper.fromPlaidAccountEntitiy(account);
    return this.httpProvider
      .post(`${APP_CONFIG.remittanceApiBaseUrl}account/bank/create`, entity);
  }

  async isBankAccountAddAllowed(): Promise<BankAccountAddAllowed>{
    const paymentAccount = await this.getPaymentAccounts(false);
    let unverifiedAccounts = 0;
    let activeAccounts = 0;
    if (paymentAccount){
      paymentAccount.bankAccounts.forEach((bankAccount)=>{
        if (bankAccount.verificationStatus !== BankAccountVerificationStatus.Verified && bankAccount.isDisabled == false)
          ++unverifiedAccounts;
        if (bankAccount.isDisabled == false)
          ++activeAccounts;
      });
      if (unverifiedAccounts >= 2) {
        return BankAccountAddAllowed.MaxUnverifiedBankAccountsReached;
      }
      else if (activeAccounts >= this.maxBanksAllowed)
        return BankAccountAddAllowed.MaxBankAccountsReached;
    }
    return BankAccountAddAllowed.Allowed;
  }

  validateCard(card: ICard): Promise<IValidateCardResponseEntity> {
    if (!APP_CONFIG.region.paymentProvider) {
      LoggerProvider.error(this, LoggerEvent.Payment, 'Missing payment provider for card validation');
      return null;
    }

    const signatureFields = APP_CONFIG.region.paymentProvider.getSignatureFields(card);
    if (!signatureFields) {
      LoggerProvider.error(this, LoggerEvent.Payment, 'Unable to create validate card request');
      return null;
    }

    const entity = PaymentMapper.toValidateCardEntity(card, signatureFields);
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}account/card/validate`, entity);
  }

  async postToPaymentProvider(url, entity, handler) {
    let postdata;
    if (this.platform.is('cordova'))
      postdata = entity;
    else {
      postdata = '';
      Object.keys(entity).forEach((id) => {
        if (postdata.length > 0) {
          postdata += '&';
        }
        postdata += encodeURIComponent(id) + '=' + encodeURIComponent(entity[id]);
      });
    }

    // IMPORTANT: CBS expects an urlencoded request, so we have to convert it
    // also important to avoid preflight/CORS (i.e. for Bluepay)
    const requestHeaders = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    url = this.mapProxyUrl(url, 8871);

    handler.fullResponse = true;
    const paymentResponse = await this.httpProvider.postExtern(url, postdata, requestHeaders, 'text', handler);
    return paymentResponse;
  }

  /**
   * @description Send to payment provider (i.e. silent post to Cybersource)
   */
  async sendToPaymentProvider(card: ICard, cardInfo: IValidateCardResponseEntity): Promise<string> {
    if (!APP_CONFIG.region.paymentProvider) {
      LoggerProvider.error(this, LoggerEvent.Payment, 'Missing payment provider for send to payment provider');
      return null;
    }

    const entity = APP_CONFIG.region.paymentProvider.createRequest(card, cardInfo);
    if (!entity) {
      LoggerProvider.error(this, LoggerEvent.Payment, 'Unable to create payment request');
      return null;
    }

    const handler: any = (response) => {
      return APP_CONFIG.region.paymentProvider.handleResponse(this, response);
    };
    let paymentResponse;
    try {
      paymentResponse = await this.postToPaymentProvider(cardInfo.partnerTransactionUrl, entity, handler);
    } catch (ex) {
      throw this.getErrorObj(ex);
    }
    if (!paymentResponse)
      return null;

    let paymentResponseCvn;
    if (!this.platform.is('cordova') && APP_CONFIG.region.defaultCountryFrom !== 'CA') {
      paymentResponseCvn = paymentResponse + "&req_merchant_defined_data59=" + (card.cvn ? "true" : "false") + "&req_merchant_defined_data58=App";
    } else if (typeof paymentResponse === 'object' && paymentResponse !== null) {
      paymentResponse['req_merchant_defined_data58'] = 'App';
      paymentResponse['req_merchant_defined_data59'] = (card.cvn ? 'true' : 'false');
      paymentResponseCvn = paymentResponse;
    }

    return APP_CONFIG.region.paymentProvider.forwardResponse(this, APP_CONFIG.remittanceApiBaseUrl, card, paymentResponseCvn);
  }

  clientSideAuthorization(order: IOrder): Promise<any> {
    return APP_CONFIG.region.paymentProvider.clientSideAuthorization(this, APP_CONFIG.remittanceApiBaseUrl, order);
  }

  async authorize3DSV2CardFiserv(orderProvider, order: IOrder, validationOrder: IValidateOrderResponse, sendFlowProvider) {
    return new Promise(async (resolve, reject) => {
      const payerAuthOrder = { ...order };
      payerAuthOrder.payerAuthSetupReferenceId = validationOrder.referenceId;
      payerAuthOrder.transactionId = validationOrder.transactionId;
      let validation = null;
      try {
        validation = await orderProvider.validateOrder(payerAuthOrder);
      }
      catch (ex) {
        validation = ex.error;
        try {
          if (order && orderProvider && ex)
            await this.generateOrderVerificationFailedEvent(order, ex, orderProvider, sendFlowProvider);
        }
        catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.Payment, 'Error when firing TransactionVerificationFailed');
        }
        if (validation && validation.errors && validation.errors.length > 0 && validation.errors[0].errorCode == ErrorCodeType.CardNotEnrolledInPayerAuthentication) {
          LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to validate order with reference ID', validation);
          reject(ex);
        }
        else if (validation && validation.errors && validation.errors.length > 0 && validation.errors[0].errorCode === ErrorCodeType.InvalidPaymentMethod) {
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.TransferResendFailed, analyticsData: {
              location: location,
              errorType: 'InvalidPaymentMethod'
            }
          });
          let errorObj = { errorCode: ErrorCodeType.InvalidPaymentMethod };
          reject(errorObj); //send errorcode to the catch
        }
      }

      const transactionId = validation.transactionId; // update it with latest transaction ID since it may have changed
      const paTransactionId = payerAuthOrder.payerAuthSetupReferenceId;
      if (validation && validation.payerAuthenticateResponse && validation.payerAuthenticateResponse.acsUrl && validation.payerAuthenticateResponse.paReq && validation.payerAuthenticateResponse.paTransactionId) {
        // friction - Challenge Flow
        LoggerProvider.info(this, 'Challenge Flow - proceed with ACS');
        validation.processor = ICardProcessor.fiserv;
        validation.threeDsType = ICardThreeDSecureType.secure3dsMethodBlock;
        const ex = { error: validation };
        // return reject to go through the send-paymentauth flow
        reject(ex);
      }
      else if (validation && !validation.payerAuthenticateResponse) {
        // frictionless
        LoggerProvider.info(this, 'Frictionless Flow - proceed with order creation');
        try {
          const createOrder = { ...order };
          createOrder.transactionId = transactionId;
          createOrder.paTransactionId = paTransactionId;
          resolve(createOrder);
        }
        catch (ex) {
          LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to complete payer authentication');
          reject(ex);
        }
      }
      else {
        LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to validate order with reference ID', validation);
        reject();
      }
    });
  }

  authorize3DSV2Card(orderProvider, order, accessToken, transactionId, referenceId, sendFlowProvider) {
    return new Promise(async (resolve, reject) => {
      try {
        if (typeof (Cardinal) !== 'undefined') {
          Cardinal.off('payments.validated');
          Cardinal.off('ui.inline.setup');
          Cardinal.off('payments.setupComplete');
        }

        if (this.utilsProvider.hasFeatureFlagUnsafe(FeatureFlagTypes.enable3dsCardinalBridge))
          await this.scriptProvider.loadCardinal(true);
        else
          await this.scriptProvider.loadCardinal();

        if (typeof (Cardinal) === 'undefined') {
          LoggerProvider.error(this, LoggerEvent.ProviderException, 'Cardinal is missing !');
          reject();
          return;
        }

        /*
        const trxRef = this.generateTrxRef();
        card.transactionReference = trxRef;
        */

        // init transaction here (songbird)
        Cardinal.configure({
          timeout: 8000,
          payment: {
            framework: 'inline'
          },
          logging: {
            level: "off"
          }
        });

        let authorized = false;
        let paTransactionId = null;
        const authContainer = document.createElement('div');
        Cardinal.on('ui.inline.setup', (htmlTemplate, details, resolveSetup, rejectSetup) => {
          if (htmlTemplate) {
            LoggerProvider.info(this, 'Cardinal setup inline UI successful', details);
            authContainer.innerHTML = htmlTemplate;
            resolveSetup();
          }
          else {
            LoggerProvider.error(this, LoggerEvent.Payment, 'Cardinal inline UI setup failed', details);
            rejectSetup();
            reject();
          }
        });

        let inprogress = false;
        Cardinal.on('payments.validated', async (data, jwt) => {
          if (!data) {
            LoggerProvider.error(this, LoggerEvent.Payment, 'Cardinal payments validated without data', data);
            reject();
          }
          else if (data.ActionCode === 'ERROR' || data.ErrorNumber != 0) {
            LoggerProvider.error(this, LoggerEvent.Payment, 'Cardinal payments validated error', data);
            reject();
          }
          else if (data.Payment && data.Payment.ExtendedData && data.Payment.ExtendedData.ChallengeCancel === '01') {
            LoggerProvider.error(this, LoggerEvent.Payment, 'Manually cancelled payment', data);
            resolve(null);
          }
          else if (jwt && paTransactionId) {
            if (inprogress) // Cardinal sometimes sends it several times (I think it wasn't like that in the beginning and the behaviour changed recently)
              LoggerProvider.info(this, 'Cardinal payments validated again although it is already in progress', data);
            else {
              LoggerProvider.info(this, 'Cardinal payments validated', data);
              inprogress = true;

              try {
                authorized = true;
                const createOrder = { ...order };
                createOrder.payerAuthenticationResponse = jwt;
                createOrder.transactionId = transactionId;
                createOrder.paTransactionId = paTransactionId;
                resolve(createOrder);
              }
              catch (ex) {
                LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to complete payer authentication');
                reject(ex);
              }
            }
          }
          else {
            LoggerProvider.error(this, LoggerEvent.Payment, 'Cardinal payments validated unknown error', data);
            reject();

          }
        });

        Cardinal.on('payments.setupComplete', async (setupCompleteData) => {
          LoggerProvider.info(this, 'Cardinal setup complete', setupCompleteData);

          try {
            LoggerProvider.info(this, 'Start Cardinal session ID: ' + setupCompleteData.sessionId);

            const payerAuthOrder = { ...order };
            payerAuthOrder.payerAuthSetupReferenceId = referenceId;
            payerAuthOrder.transactionId = transactionId;
            let validation = null;
            try {
              validation = await orderProvider.validateOrder(payerAuthOrder);
            }
            catch (ex) {
              validation = ex.error;
              try {
                if (order && orderProvider && ex)
                  await this.generateOrderVerificationFailedEvent(order, ex, orderProvider, sendFlowProvider);
              }
              catch (ex) {
                LoggerProvider.exception(this, ex, LoggerEvent.Payment, 'Error when firing TransactionVerificationFailed');
              }
              if(ex && ex.errorCode == ErrorCodeType.CardNotEnrolledInPayerAuthentication){
                let error = ex && ex.error? ex.error: ex;
                LoggerProvider.error(this, LoggerEvent.PageException, 'CardNotEnrolledInPayerAuthentication');
                this.routeProvider.openModal(this.routeProvider.pages.SendFailedPage, {missing: ex.errorCode, error: error, digitalError: true, goBackToSendSummary: true});
                return;
              }

              else if(validation && validation.errors && validation.errors.length > 0 && validation.errors[0].errorCode === ErrorCodeType.InvalidPaymentMethod) {
                this.analytics.trackEvent({ eventName: AnalyticsEventType.TransferResendFailed, analyticsData: {
                  location: location,
                  errorType: 'InvalidPaymentMethod'
                }});
                let errorObj = { errorCode: ErrorCodeType.InvalidPaymentMethod};
                reject(errorObj); //send errorcode to the catch
              }
            }

            transactionId = validation.transactionId; // update it with latest transaction ID since it may have changed
            if (validation && validation.payerAuthenticateResponse && validation.payerAuthenticateResponse.acsUrl && validation.payerAuthenticateResponse.paReq && validation.payerAuthenticateResponse.paTransactionId) {
              LoggerProvider.info(this, 'Requires payment authorization - proceed with CCA');

              paTransactionId = validation.payerAuthenticateResponse.paTransactionId
              await this.routeProvider.openModal(this.routeProvider.pages.HrefPage, { contentElement: authContainer }, () => {
                if (!authorized)
                  resolve(null);
              });
              const details = {
                'OrderDetails': {
                  'TransactionId': paTransactionId
                }
              };
              Cardinal.continue('cca', {
                'AcsUrl': validation.payerAuthenticateResponse.acsUrl,
                'Payload': validation.payerAuthenticateResponse.paReq
              }, details);
            }
            else if (validation && !validation.payerAuthenticateResponse) {
              // frictionless
              LoggerProvider.info(this, 'Frictionless payment - proceed with order creation');
              try {
                authorized = true;
                const createOrder = { ...order };
                createOrder.transactionId = transactionId;
                resolve(createOrder);
              }
              catch (ex) {
                LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to complete payer authentication');
                reject(ex);
              }
            }
            else {
              LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to validate order with reference ID', validation);
              reject();
            }
          }
          catch (ex) {
            LoggerProvider.error(this, LoggerEvent.Payment, 'Failed to allocate card transaction');
            reject(ex);
          }
        });

        this.scriptProvider.initCardinal(accessToken);
      }
      catch (ex) {
        reject(ex);
      }
    });
  }

   /**
   * @description Edit a current customer's bank account information.
   */
  updateBankAccount(account: BankAccountUpdate): Promise<any> {
    return this.httpProvider
      .post(`${APP_CONFIG.remittanceApiBaseUrl}account/bank/edit`, account);
  }

  updateCard(cardInfo: CardUpdate): Promise<any> {
    return this.httpProvider
      .put(`${APP_CONFIG.remittanceApiBaseUrl}account/card`, cardInfo);
  }


  /**
   * @description Remove a specific account tied to a customer's account.
   */
  deleteAccount(id: number): Promise<any> {
    return this.httpProvider.delete(`${APP_CONFIG.remittanceApiBaseUrl}account/delete/${id}`);
  }


  /**
   * @description Verify bank account
   * @param {IBankAccount} bankAccount
   * @param {string} verificationDeposit1
   * @param {string} verificationDeposit2
   * @returns {Promise<any>}
   */
  verifyBankAccount(verifyBankAccount: IBankVerification): Promise<any> {
    const entity = PaymentMapper.toBankVerificationEntity(verifyBankAccount);
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}account/bank/verify`, entity);
  }

  // !!! THIS FUNCTION IS USED BY THE EXTERNAL PAYMENT PROVIDERS PAYMENT.CYBERSOURCE.TS AND PAYMENT.BLUEPAY.TS SO ALTHOUGH IT DOESN'T SHOW REFERENCES IT HAS TO REMAIN HERE !!!
  logPaymentError(message, details, log = this, ev = LoggerEvent.Payment) {
    LoggerProvider.error(log, ev, message, details);
  }

  logPaymentInfo(message, details, log = this, ev = LoggerEvent.Payment) {
    LoggerProvider.log(log, LoggerLevel.Notice, ev, message, details);
  }

  getPlaidSettings(): Promise<any> {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/PaymentAccount/InstantBankVerification/Settings`, {})
    // .map(
    //   res => PaymentMapper.fromPaymentAccountsEntity(<IPaymentAccountsEntity>res).cards

    // );
  }

  getPlaidUpdateMode(paymentAccountId): Promise<any> {

    let customerId = this.httpProvider.getAuthCustomerId();
    const httpURLSubstring = `Customers/${customerId}/Token/${paymentAccountId}/Update`;

    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/PaymentAccount/InstantBankVerification/${httpURLSubstring}`, {})
  }

  updateBankInstantVerificationStatus(paymentAccountId, publicToken, accountID, updateAccountMetaData): Promise<IPlaidUpdateResponse> {

    const payLoad: IPlaidRequest = {
      "PaymentAccountId": paymentAccountId,
      "PublicToken": publicToken,
      "AccountId": accountID,
      "MetaData": updateAccountMetaData
    }


    const httpURLSubstring = `account/bank/updateinstantverificationstatus`;

    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}${httpURLSubstring}`, payLoad)

  }

  updatePlaidAccountStatus(payLoad) {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/PaymentAccount/UpdateAccountStatus`, payLoad)
  }

  mapProxyUrl(url, localPort = 8447) {
    if (!this.platform.is('cordova') && (<any>APP_CONFIG).localProxy) {
      let path = url.substring(url.lastIndexOf('.') + 5);
      if (path.length > 0)
        path = '/' + path;
      if ((<any>APP_CONFIG).localProxy)
        url = `http://127.0.0.1:${localPort}${path}`;
      else
        url = `${window.location.origin}${path}`;
    }
    return url;
  }

  verifyPayerAuthentication(xid): Promise<any> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}account/card/verify-payer-authentication/${xid}`)
  }

  validateBankAccount(localRoutingCode: string, accountNumber: string): Promise<any> {
    const params = { localRoutingCode: localRoutingCode, accountNumber: accountNumber }
    return this.httpProvider.post(APP_CONFIG.remittanceApiBaseUrl + 'account/bank/validate', params);
  }

  async getAccountById(accountId: number): Promise<IBankAccount | ICard | null> {
    const accounts = await this.getPaymentAccounts();
    for (const accountType in accounts) {
      console.log(`${accountType}: ${accounts[accountType]}`);
      accounts[accountType].forEach(account => {
        if (account.accountId === accountId)
          return account;
      });
    }
    return null;
  }

  getErrorObj(ex: any) {
    let htmlCreation = document.createElement('form')
    htmlCreation.innerHTML = ex;
    let form = htmlCreation.elements;
    let message;
    let errorCodeType;
    for (let i = 0; i < form.length; i++) {
      if (form[i].nodeName === "INPUT" && form[i].id === "message") {
        message = (form[i] as HTMLInputElement).value;
        errorCodeType ="ExceptionFromCyberSource"
      }
      if (form[i].nodeName === "INPUT" && (form[i] as HTMLInputElement).defaultValue === "bluepay01") {
        message = (form[i] as HTMLInputElement).value;
        errorCodeType ="ExceptionFromBluepay"
      }
    }
    let errorObj;
    return errorObj = { errorCode: errorCodeType, errorMessage: message }
  }

  async generateOrderVerificationFailedEvent(order, ex, orderProvider, sendFlowProvider) {
    // TODO !!!!!!!!!!!!!!!!!!!! duplicate code - should be moved to a function so it corresponds with send-finish
    if (ex) {
      let missing = null;
      if (ex.error)
        missing = ex.error.missingTransactionInfo;

      if (missing === MissingTransactionInfoType.RequiresPayerAuthenticationCheck || ex.errorCode === ErrorCodeType.InvalidPaymentMethod || ex.errorCode === ErrorCodeType.SarBsaValidation) {
        LoggerProvider.info(this, 'Skipped event for transaction verification failed because of error code');
        return;
      }
    }

    try {
      let country = await this.resourceProvider.getCountryByValue(order.countryTo)
      const paymentObj = orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
      this.loginProvider.getCustomerAsync().then((customer) => {
        this.analytics.trackEvent({
          eventName: AnalyticsEventType.TransactionVerificationFailed, analyticsData: {
            destinationCity: (order.agentTo) ? order.agentTo.city : null,
            destinationCountry: order.countryTo,
            errorType: (ex && ex.error && ex.error.missingTransactionInfo) ? ex.error.missingTransactionInfo :
              (ex && ex.error && ex.error.errors && ex.error.errors.length > 0) ? ex.error.errors[0].errorCode : ex.errorCode,
            errorSubType: (ex && ex.error && ex.error.errors && ex.error.errors.length > 0) ? ex.error.errors[0].key : ex.errorKey,
            errorDescription: (ex && ex.error && ex.error.errors && ex.error.errors.length > 0) ? ex.error.errors[0].message : ex.errorMessage,
            sendAmountUSD: SegmentProvider.formatNumber(order.sendAmountUSD),
            sendCurrency: order.currencyFrom,
            senderCountry: (order.countryFrom) ? order.countryFrom : APP_CONFIG.region.defaultCountryFrom,
            feeUSD: order.customerFeeUSD,
            paymentAgent: (customer && customer.AgentNameNo) ? customer.AgentNameNo : null,
            paymentMethod: order.paymentMethod,
            payoutMethod: orderProvider.getAnalyticsDeliveryMethodType(order.deliveryMethodInternal, order.deliveryMethodAlias),
            payoutAgent: (order.agentTo) ? SegmentProvider.formatInt(order.agentTo.id) : null,
            payoutAmount: SegmentProvider.formatNumber(order.amountTo),
            payoutBankName: order.deliveryMethodInternal === DeliveryMethodType.BankDeposit ? order.recipient.bankName : null,
            payoutCountryType: (country && country.isOpenPaymentCountry !== null) ? country.isOpenPaymentCountry : null,
            payoutCurrency: order.currencyTo,
            payoutLocationID: (order.agentToLocation) ? SegmentProvider.formatInt(order.agentToLocation.locationId) : null,
            product: 'App',
            promoName: order.promoCode ? order.promoCode : (order.promoId ? order.promoId : null),
            rate: order.exchangeRate,
            totalAmountPaid: order.totalAmount,
            transferReason: order.transferReason,
            userId: this.httpProvider.getAuthUserId(),
            orderId: order.transactionId,
            pendingWalletType: sendFlowProvider.getPendingWalletType(),
             ...paymentObj
          }
        });
      });
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Payment, 'Error when firing TransactionVerificationFailed');
    }
  }


  getCardIcon(card, onlyCardIcon = false) {
    if (!card)
      return '';
    if (card.isDisabled && !onlyCardIcon)
      return 'assets/icons/ic_item_disabled.svg';
    if (card.isExpired && !onlyCardIcon)
      return 'assets/icons/ic_item_expired_circular.svg';
    if (card.type === PaymentCardType.MasterCard || card.accountType === PaymentCardType.MasterCard)
      return 'assets/img/payment-card/mastercard.svg';
    if (card.type === PaymentCardType.Visa || card.accountType === PaymentCardType.Visa)
      return 'assets/img/payment-card/visa.svg';
    if (card.type === PaymentCardType.Discover || card.accountType === PaymentCardType.Discover)
      return 'assets/img/payment-card/discover.svg';

    return 'assets/icons/card_grey.svg';
  }

  async getPaymentMethodByPspPaymentMethod(paymentMethod) {
    const paymentMethods = await this.resourceProvider.getPaymentMethods();
    return this.getPaymentMethodByPspPaymentMethodSync(paymentMethod, paymentMethods);
  }

  getPaymentMethodByPspPaymentMethodSync(paymentMethod, paymentMethods = null) {
    if (!paymentMethods)
      paymentMethods = this.resourceProvider.paymentMethods;
    if (!paymentMethods)
      return {};

    if (paymentMethod.type === 'scheme') {
      let paymentMethodValue = PaymentMethodType.CreditCard;
      if (paymentMethod.fundingSource && paymentMethod.fundingSource.toLowerCase() === 'debit')
        paymentMethodValue = PaymentMethodType.DebitCard;
      return paymentMethods.find((m) => m.value == paymentMethodValue);
    } else if (paymentMethod.type === PaymentMethodType.GooglePay.toLowerCase() || paymentMethod.type === PaymentMethodType.ApplePay.toLowerCase()) {
      if (this.utilsProvider.isAppleOrGooglePaymentCreditAllowed() && this.checkIfAppleOrGoogleCreditEnabled(paymentMethods)) {
        return paymentMethods.find((m) => m.pspPaymentMethod == paymentMethod.type && (m.value == PaymentMethodType.GooglePayCredit || m.value == PaymentMethodType.ApplePayCredit));
      } else {
        return paymentMethods.find((m) => m.pspPaymentMethod == paymentMethod.type);
      }
    } else
      return paymentMethods.find((m) => m.pspPaymentMethod == paymentMethod.type);
  }

  async isDiscoverEnabled(): Promise<boolean> {
    const paymentAccounts = await this.getPaymentAccounts();
    return !!paymentAccounts && !!paymentAccounts.allowedCardBrands && paymentAccounts.allowedCardBrands.includes(CARD_TYPE.DISCOVER.Key);
  }

  isDiscoverCredit(cardInfo: CardInfo): boolean {
    return !!cardInfo && cardInfo.cardBrand === CARD_TYPE.DISCOVER.Key && cardInfo.paymentMethod === PaymentMethodType.CreditCard;
  }

  
  showDiscoverCreditNotSupportedModal(chooseAnotherCard: () => void, close: () => void) {
    let action: () => void = null;
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      showFullPage: false,
      image: 'assets/icons/close-circle.svg',
      imageWithCircle: true,
      title: this.translate.instant('page.paymentMethods.discover.creditNotSupported.title'),
      subtitle: this.translate.instant('page.paymentMethods.discover.creditNotSupported.body'),
      primaryButtonText: this.translate.instant('page.paymentConfirmationPage.chooseAnotherCardButton'),
      primaryAction: () => { action = chooseAnotherCard; },
      dismissFromPrimary: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('global.closeButton'),
      secondaryAction: () => { action = close; },
      dismissFromSecondary: true
    }, () => action && window.requestAnimationFrame(action));
  }

  async getAllowedCards() {
    let isMaestroEnable = false;
    const [agent, isDiscoverEnabled] = await Promise.all([
      this.utilsProvider.getAgentFeatures().toPromise(),
      this.isDiscoverEnabled()
    ]);
    if (agent && agent.length > 0) {
      isMaestroEnable = agent[0].AgentSettings.find(setting =>
        (setting.SettingType === AGENT_SETTINGS.MAESTRO_ALLOWED.Type) && (setting.SettingValue === AGENT_SETTINGS.MAESTRO_ALLOWED.Value));
    }
    const cardsAllowed = [
      { type: CARD_TYPE.VISA, src: 'assets/img/payment-card/visa.svg', enabled: true },
      { type: CARD_TYPE.MASTERCARD, src: 'assets/img/payment-card/mastercard.svg', enabled: true }
    ];
    if (isMaestroEnable) cardsAllowed.push({ type: CARD_TYPE.MAESTRO, src: 'assets/img/payment-card/maestro.svg', enabled: true });
    if (isDiscoverEnabled) cardsAllowed.push({ type: CARD_TYPE.DISCOVER, src: 'assets/img/payment-card/discover.svg', enabled: true });
    return cardsAllowed;
  }

  async getBillingStates() {
    const fields = await this.formProvider.getFieldsCard();
    const stateFields = fields.find(field => field.id === "billingState");
    if (stateFields)
        return stateFields.options;
    return null;
  }

  async createBillingAddress(customer?, card?) {

    // createBillingAddress from Card or Customer object
    if (card) {
      return {
        'firstName': card.firstName,
        'lastName': card.lastName,
        'billingCountry': card.country,
        'billingAddress': card.street,
        'billingCity': card.city,
        'billingState': card.stateProvince ? card.stateProvince : '',
        'billingStateProvince': card.stateProvince ? card.stateProvince : '',
        'billingPostalCode': card.postCode,
        'stateLabel': card.stateProvince ? card.stateProvince : '',
      };
    }
    else if (customer){
      const states = await this.getBillingStates();

      let state = null;
      if (states)
        state = states.find(state => state.value === customer.address.stateProvince);
      return {
        'firstName': customer.firstName,
        'lastName': customer.lastName,
        'billingCountry': customer.address.country,
        'billingAddress': customer.address.street,
        'billingCity': customer.address.city,
        'billingState': state ? state.value : '',
        'billingStateProvince': customer.address.stateProvince,
        'billingPostalCode': customer.address.postCode,
        'stateLabel': state ? state.text : ''
      };
    }
  }

  buildBillingAddress(billingAddress) {
    if (!billingAddress)
      return '';
    const billingPostalCode = billingAddress['billingPostalCode'] != null ? billingAddress['billingPostalCode'] : "";
    const country = billingAddress['billingCountry'];
    let state = '';
    if (country === 'US' || country === 'CA') // for US and CA try to use the short form is possible
      state = billingAddress['billingState'];
    if (!state)
      state = billingAddress['stateLabel'];
    if (!state)
      state = billingAddress['billingStateProvinence'];
    if (!state)
      state = billingAddress['billingState'];
    return '<div>' + billingAddress['billingAddress'] + ', </div><div>' + billingAddress['billingCity'] + ', ' + state + ' ' + billingPostalCode + '</div>';
  }

  mapBillingAddress(billingAddress, toPsp: boolean) {
    let map: any = {
      street: 'billingAddress',
      postalCode: 'billingPostalCode',
      city: 'billingCity',
      country: 'billingCountry',
      stateOrProvince: 'billingStateProvince',
    };

    if (toPsp) {
      const reverseMap = {};
      for (const key in map) {
        reverseMap[map[key]] = key;
      }
      map = reverseMap;
    }

    const result = {};
    for (const key in map) {
      const resultKey = map[key];
      if (billingAddress && billingAddress[key])
        result[resultKey] = billingAddress[key];
      else
        result[resultKey] = '';
    }
    return result;
  }

  getPspPaymentMethod(pspPaymentMethods, pspPaymentMethod, paymentMethod) {
    if (pspPaymentMethod === 'scheme') {
      let fundingSource = 'credit';
      if (paymentMethod === PaymentMethodType.DebitCard)
        fundingSource = 'debit';
      return pspPaymentMethods.find((method) => method.type === pspPaymentMethod && method.fundingSource && method.fundingSource.toLowerCase() === fundingSource);
    }
    else
      return pspPaymentMethods.find((method) => method.type === pspPaymentMethod);
  }

  showCardAccountLimitReached(paymentMethod) {
    LoggerProvider.info(this, 'Show card account limit reached');
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: "assets/imgs/rejected-card.svg",
        title: (paymentMethod === PaymentMethodType.CreditCard) ? this.translate.instant('page.errorMessage.maxCreditCardsTitle') : this.translate.instant('page.errorMessage.maxDebitCardsTitle'),
        subtitle: (paymentMethod === PaymentMethodType.CreditCard) ? this.translate.instant('page.errorMessage.ria_errormessage_maxcreditcardsbody') : this.translate.instant('page.errorMessage.ria_errormessage_maxdebitcardsbody'),
        primaryButtonText: this.translate.instant('global.gotItButton'),
        showPrimaryButton: true,
        primaryAction: null
      },
      null,
      'max-card-limit-modal'
    );
  }

  notifyPossibleChanges() {
    this.lastPaymentAccountsListDate = null;
  }

  has3DSRedirect(order) {
    if (APP_CONFIG.region.isEu && order && (order.paymentMethod === PaymentMethodType.CreditCard || order.paymentMethod === PaymentMethodType.DebitCard))
      return true;
    return false;
  }

  async navigateBackToPspPage(orderProvider, order, params = {}) {
    // new validate promise to create a new transaction ID otherwise no new order number will be created
    try {
      order = { ...order };
      delete order.pspProcessor;
      delete order.pspDeviceCollectionReferenceId;
      delete order.pspPaymentSessionData;

      let validateOrderPromise = orderProvider.validateOrder(order);

      // if payment method is a card wait for the validate to ensure stored payment methods are uptodate since eventually one got added in-between
      if (order.pspPaymentMethod === 'scheme') {
        this.loading.show();
        const validation = await validateOrderPromise;
        this.loading.hide();
        validateOrderPromise = null;
        order = { ...order, ...validation };
        orderProvider.setCurrentOrder(order);
      }

      params = {
        paymentMethods: order.pspPaymentMethods,
        paymentSession: order.pspSession,
        validateOrderPromise,
        ...params
      };
      if (this.routeProvider.hasPage(this.routeProvider.pages.SendPspPage))
        await this.routeProvider.replace(this.routeProvider.pages.SendPspPage, params, this.routeProvider.pages.SendPspPage, false, true); // TODO DOUBLE CHECK !!!!!!!!!!!!!!!!!!!! validateOrderPromise ???????????
      else
        await this.routeProvider.replace(this.routeProvider.pages.SendPspPage, params);
    } catch (ex) {
      this.loading.hide();
      RmtPopupComponent.showException(this, ex, this.translate.instant('global.myRia.jsonContent.OopsInfo'));
      this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
    }
  }

  getAllowEditBillingAddress() {
    const disable = this.utilsProvider.hasFeatureFlagUnsafe(FeatureFlagTypes.DisableEditBillingAddress);
    if (disable)
      return false;
    return true;
  }

  isPaymentMethodAllowed(paymentMethod, order, log = false) {
    let result = true;
    if (APP_CONFIG.region.defaultCountryFrom === 'US' &&
        paymentMethod === PaymentMethodType.CreditCard &&
        order && order.countryTo === 'PK')
        result = false;

    if (!result && log)
      LoggerProvider.info(this, `Payment method not allowed: ${paymentMethod}`)
    return result;
  }

  showPaymentErrorOrFailModal(orderProvider, order, isSendAgain, isAuthFailed: boolean = false) {
    let title = this.translate.instant('page.errorMessage.unableToCreateTitle');
    let message = this.translate.instant('page.errorMessage.unableToCreateBody');
    if(isAuthFailed) {
      title = this.translate.instant('page.errorMessage.paymentDeclinedTitle');
      message = this.translate.instant('page.errorMessage.paymentDeclinedBody');
      if(order.pspPaymentMethod === DirectBankBrand.Blik.toLowerCase()) {
        message = this.translate.instant('page.errorMessage.paymentDeclinedBlik.description');
      }
    }
    this.routeProvider.replace(this.routeProvider.pages.ModalAlertPage, {
      image: 'assets/imgs/red-error_bg_color.svg',
      title: title,
      msg: message,
      dismissFromPrimary: false,
      dismissFromSecondary: false,
      secondaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.changePaymentMethod'),
      secondaryAction: () => {
        this.rerouteToChangePaymentMethod(orderProvider, order, isSendAgain);
      },
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      showSecondaryButton: true,
      showFullPage: true,
      verticalCenter: true,
      isModal: false,
      primaryAction:() => {
        this.gotoPspPage(orderProvider, order, isSendAgain);
      }
    });
  }

  showPaymentCancelledModal(orderProvider, order, isSendAgain) {
    this.routeProvider.replace(this.routeProvider.pages.ModalAlertPage, {
      image: 'assets/imgs/red-error_bg_color.svg',
      title: this.translate.instant('page.errorMessage.transferCancelledTitle'),
      msg: this.translate.instant('page.errorMessage.transferCancelledBody'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      primaryAction: () => {
        this.gotoPspPage(orderProvider, order, isSendAgain);
      },
      secondaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.changePaymentMethod'),
      showSecondaryButton: true,
      showFullPage: true,
      verticalCenter: true,
      isModal: false,
      secondaryAction:() => {
        this.rerouteToChangePaymentMethod(orderProvider, order, isSendAgain);
      }
    });
  }

  async gotoPspPage(orderProvider, order, isSendAgain) {
    this.loading.show();
    this.navigateBackToPspPage(orderProvider, order, {
      isSendAgain
    });
    await this.routeProvider.transitionPromise;
    this.loading.hide();
  }

  rerouteToChangePaymentMethod(orderProvider, order, isSendAgain, skipLoadingIndicator: boolean = false) {
    if (!skipLoadingIndicator)
      this.loading.show();
    this.navigateBackToPspPage(orderProvider, order, {
      isSendAgain,
      openChangePaymentMethodScreen: true,
      onBackCallback: () => {
        this.routeProvider.setRoot(this.routeProvider.pages.SendSummaryPage);
      }
    });
    if (!skipLoadingIndicator) // skips closing the loading indicator when there is one already active and we still need it
      this.loading.hide();
  }

  getFeesForAppleOrGoogle(paymentMethods, customerFee = null) {
    let creditItem = null;
    let debitItem = null;
    if (paymentMethods && this.utilsProvider.isAppleOrGooglePaymentCreditAllowed()) {
      if (this.platform.is('ios')) {
        const applePayIndex = paymentMethods.findIndex(obj => obj.value === PaymentMethodType.ApplePay);
        const applePayCreditIndex = paymentMethods.findIndex(obj => obj.value === PaymentMethodType.ApplePayCredit);
        if (applePayIndex > -1 && applePayCreditIndex > -1) {
          creditItem = paymentMethods[applePayCreditIndex];
        }
        if (applePayIndex > -1)
          debitItem = paymentMethods[applePayIndex];
      } else {
        const googlePayIndex = paymentMethods.findIndex(obj => obj.value === PaymentMethodType.GooglePay);
        const googlePayCreditIndex = paymentMethods.findIndex(obj => obj.value === PaymentMethodType.GooglePayCredit);
        if (googlePayIndex > -1 && googlePayCreditIndex > -1) {
          creditItem = paymentMethods[googlePayCreditIndex];
        }
        if (googlePayIndex > -1)
          debitItem = paymentMethods[googlePayIndex];
      }
    }

    // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    // we should take customerFee but since the BE doesn't apply the promo on all payment methods yet one of them can be 0 which can be confusing
    let debitFee = '';
    let creditFee = '';
    if (debitItem)
      debitFee = debitItem.originalCustomerFee > 0 ? debitItem.originalCustomerFee : debitItem.customerFee;
    if (creditItem)
      creditFee = creditItem.originalCustomerFee > 0 ? creditItem.originalCustomerFee : creditItem.customerFee;

    const customerFeeFromOrder = customerFee != null ? customerFee.toString() : '';
    return {
      debitFee,
      debitItem,
      creditFee: customerFeeFromOrder == "0" ? customerFeeFromOrder : creditFee,
      creditItem
    }
  }

  checkIfAppleOrGoogleCreditEnabled(paymentMethods) {
    if(paymentMethods) {
      const googlePayCreditIndex = paymentMethods.findIndex(obj => obj.value === PaymentMethodType.GooglePayCredit);
      const applePayCreditIndex = paymentMethods.findIndex(obj => obj.value === PaymentMethodType.ApplePayCredit);
      return googlePayCreditIndex > -1 || applePayCreditIndex > -1;
    } else {
      return false;
    }
  }

  getAppleOrGoogleCreditValue(value) {
    if (this.platform.is('ios') && value === PaymentMethodType.ApplePay) {
      value = PaymentMethodType.ApplePayCredit;
    } else if (value === PaymentMethodType.GooglePay) {
      value = PaymentMethodType.GooglePayCredit;
    }
    return value;
  }

  formatFee(item, currencyFormatter, customerFee, currency) {
    if (currency)
      currency = currency.toUpperCase();
    let fee = currencyFormatter.transform(customerFee, currency ? currency : '', false, true);

    // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    // ideally remove the first if and only keep the second but at the moment we don't get reliable fees with promos for all payment methods
    // hence it would be more confusing to show the promotion fee only for the selected payment method and not for the others
    // consequently if anywhere a promo is used just show the full fee and the discount will be displayed outside of this function
    if (item.originalCustomerFee > 0) {
      fee = currencyFormatter.transform(item.originalCustomerFee, currency ? currency : '', false, true);
    } else if (customerFee != null && typeof(customerFee) !== 'undefined' && item.originalCustomerFee &&
        item.originalCustomerFee > 0 && customerFee.toString() !== item.originalCustomerFee.toString()) {
      let feeHtml;
      feeHtml = `<span class="old-value">${currencyFormatter.transform(item.originalCustomerFee, currency ? currency : '', false, true)}</span>`
      feeHtml += `<span class="promo-value">${fee} ${currency}</span>`
      return feeHtml;
    }

    return fee + ' ' + currency;
  }

  getFeeMessage(item: any, paymentMethods: any, currencyFormatter: any, sendCurrency: string, translate: any) {
    let customerFee = item.customerFee ? item.customerFee : ((typeof(item.fee) !== 'undefined') ? item.fee : item.customerFee);

    const paymentMethod = item.type ? item.type : item.paymentMethod;

    let fee = this.formatFee(item, currencyFormatter, customerFee, sendCurrency);
    if (paymentMethods &&
        (paymentMethod && (paymentMethod.toLowerCase() === PaymentMethodType.ApplePay.toLowerCase() ||
        paymentMethod.toLowerCase() === PaymentMethodType.GooglePay.toLowerCase()))) {

      const fees = this.getFeesForAppleOrGoogle(paymentMethods);
      const creditCustomerFee = fees.creditFee;
      let debitCustomerFee = fees.debitFee;
    const isCreditFeeAvailable = creditCustomerFee != null && creditCustomerFee != undefined && creditCustomerFee.toString()
      if(isCreditFeeAvailable && debitCustomerFee.toString() !== creditCustomerFee.toString()) {
        if (creditCustomerFee.toString() === '0')
          debitCustomerFee = creditCustomerFee;

        const creditFee = this.formatFee(fees.creditItem, currencyFormatter, creditCustomerFee, sendCurrency);
        const debitFee = this.formatFee(fees.debitItem, currencyFormatter, debitCustomerFee, sendCurrency);
        return `<span class="rmt-font Caption semibold fee-text">${creditFee} <span class="rmt-font Caption regular"> ${translate.instant('page.topUpPage.feeLabel').toLowerCase()} (${translate.instant('page.paymentAccountDetailsPage.paymentMethods.creditCard').toLowerCase()})</span></span> <br />
                <span class="rmt-font Caption semibold debitfee-text">${debitFee} <span class="rmt-font Caption regular"> ${translate.instant('page.topUpPage.feeLabel').toLowerCase()} (${translate.instant('page.paymentAccountDetailsPage.paymentMethods.debitCard').toLowerCase()})</span></span>`;
      } else {
        return `<span class="rmt-font Caption semibold fee-text">${fee} <span class="rmt-font Caption regular"> ${translate.instant('page.topUpPage.feeLabel').toLowerCase()}</span></span>`;
      }
    }
    return `<span class="rmt-font Caption semibold fee-text">${fee} <span class="rmt-font Caption regular"> ${translate.instant('page.topUpPage.feeLabel').toLowerCase()}</span></span>`;
  }

  // default title and description for BNL Mastercard not supported in IT
  showCardIssuedCountryNotAvailableModal(retunFunction = null) {
    const title = this.translate.instant('page.paymentMethods.bnlMastercard.alert.title');
    const description = this.translate.instant('page.paymentMethods.bnlMastercard.alert.description');
    const params = {
      imageWithCircle: false,
      image: "assets/imgs/modal-error.svg",
      title: title,
      subtitle: description,
      primaryButtonText: this.translate.instant('global.okButton'),
      showCloseButton: false
    };

    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, params, () => {
      retunFunction && retunFunction();
    });
  }
}


// WEBPACK FOOTER //
// ./src/providers/payment/payment.ts